import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import i18n from 'i18next';
import { action, makeObservable, observable } from 'mobx';

import { getUserLanguage } from '../../i18n/helpers';
import { resolveFlag } from '../../services/launchDarkly';
import { devLog } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { formatTime } from '../../utils/time';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import type { UserFreeSpinsData } from './types';
import { fetchUserFreeSpins } from './utils';

export class UserFreeSpinsStore extends Loadable implements IFeatureManagerStore {
  private readonly rootStore: RootStore;
  private prevLang = getUserLanguage();

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;

        const featureFlag = resolveFlag('user_free_spins_widgets_feature', true);

        if (!featureFlag) {
          return;
        }

        fetchUserFreeSpins()
          .then((freeSpinsResponse) => {
            this.setUserFreeSpinsData(freeSpinsResponse.data.data.freeSpinsData);
          })
          .catch((error) => {
            devLog('Error fetching user free spins', error);
          });
      }
    });
  }

  private dialogsQueue: string[] = [];
  private timerInterval: NodeJS.Timeout | undefined = undefined;

  dialogTypes: FeatureManagerDialogType[] = ['UserFreeSpins'];

  @observable
  public selectedFreeSpin: UserFreeSpinsData | null = null;

  @observable
  public userFreeSpinsData: UserFreeSpinsData[] = [];

  @observable
  public isDialogOpen = false;

  @observable
  public timers: Record<string, number> = {};

  @action
  public openDialog() {
    this.isDialogOpen = true;
  }

  @action
  public closeDialog() {
    this.selectedFreeSpin = null;
    this.isDialogOpen = false;
  }

  @action
  private setTimers(timers: Record<string, number>) {
    this.timers = timers;
  }

  private calculateExpirationTime(freeSpin: UserFreeSpinsData) {
    if (!freeSpin.createdAt) {
      return 0;
    }

    const createdAt = dayjs(freeSpin.createdAt);
    const createdAtPlus24h = createdAt.add(24, 'hour');
    const bonusCodeExpirationTime = freeSpin.endDateTime || freeSpin.endDate || freeSpin.expirationDate;

    // Use whichever is earlier: createdAt + 24h OR bonusEndTime
    if (!bonusCodeExpirationTime) {
      const secondsToEnd = createdAtPlus24h.diff(dayjs(), 'second');
      return Math.max(0, secondsToEnd);
    }

    const expirationTime = dayjs(bonusCodeExpirationTime);
    const endTime = expirationTime.isBefore(createdAtPlus24h) ? expirationTime : createdAtPlus24h;

    const secondsToEnd = endTime.diff(dayjs(), 'second');
    return Math.max(0, secondsToEnd);
  }

  private startTimers() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      const timers = { ...this.timers };
      Object.keys(timers).forEach((key) => {
        timers[key] = timers[key] - 1;
        if (timers[key] <= 0) {
          delete timers[key];
          this.removeFreeSpin(key);
        }
      });
      this.setTimers(timers);
    }, 1000);
  }

  @action
  public removeFreeSpin(id: string) {
    this.userFreeSpinsData = this.userFreeSpinsData.filter((spin) => spin.id !== id);
    this.dialogsQueue = this.dialogsQueue.filter((spinId) => spinId !== id);

    if (this.selectedFreeSpin?.id === id) {
      this.selectedFreeSpin = null;
      if (this.isDialogOpen) {
        this.closeDialog();
      }
    }
  }

  public getFreeSpinTimerFormatted(id: string, t?: TFunction): string {
    const timerSecs = this.timers[id];
    if (timerSecs && timerSecs > 0) {
      return formatTime(timerSecs, 's', t);
    }
    return '';
  }

  @action
  private setUserFreeSpinsData(data: UserFreeSpinsData[]) {
    const slotMachinesFrbFlag = resolveFlag('slot_machines_frb_widgets_enable', true);
    if (!slotMachinesFrbFlag) {
      this.userFreeSpinsData = data.filter((spin) => ['Barouletta Gaming', 'Minigames'].includes(spin.provider));
      return;
    }
    this.userFreeSpinsData = data;

    // initilalize timers
    const timers: Record<string, number> = {};
    this.userFreeSpinsData.forEach((spin) => {
      const secondsToEnd = this.calculateExpirationTime(spin);
      if (secondsToEnd > 0) {
        timers[spin.id] = secondsToEnd;
      }

      this.setTimers(timers);

      // Start timer interval if there are active timers
      if (Object.keys(timers).length > 0) {
        this.startTimers();
      } else if (this.timerInterval) {
        // Clear interval if no timers
        clearInterval(this.timerInterval);
        this.timerInterval = undefined;
      }
    });
  }

  @action
  public openFreeSpinDialogById(id: string) {
    const freeSpin = this.userFreeSpinsData.find((spin) => spin.id === id);
    if (freeSpin) {
      this.selectedFreeSpin = freeSpin;
      this.openDialog();
    }
  }

  public startDialogs(type?: FeatureManagerDialogType) {
    if (type === 'UserFreeSpins' && this.rootStore.authStore.authorized) {
      // Skip when a minigame is already opening — the FS that triggered this dialog is
      // being launched directly (e.g., daily-bonus claim auto-launches the wheel),
      // and the FreeSpinDialog "Claim" intermediary would render behind that popup.
      const { minigamesStore } = this.rootStore;
      if (minigamesStore.isDialogOpen || minigamesStore.loadingMinigame) {
        return false;
      }
      return this.showNextDialog();
    }

    return false;
  }

  public showNextDialog() {
    if (this.dialogsQueue.length > 0) {
      this.openFreeSpinDialogById(this.dialogsQueue.shift()!);
      return true;
    }
    return false;
  }

  public cleanup() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
    this.timers = {};
  }

  @action
  public reset() {
    this.cleanup();
    this.userFreeSpinsData = [];
    this.selectedFreeSpin = null;
    this.isDialogOpen = false;
    this.dialogsQueue = [];
    this.setLoading(false);
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    const featureFlag = resolveFlag('user_free_spins_widgets_feature', true);
    if (!featureFlag) return 'OFF';

    this.setLoading(true);
    try {
      const freeSpinsResponse = await fetchUserFreeSpins();

      this.setUserFreeSpinsData(freeSpinsResponse.data.data.freeSpinsData);
      this.dialogsQueue = this.userFreeSpinsData.map((spin) => spin.id);
      return 'READY';
    } catch (error) {
      devLog('Error fetching user free spins', error);
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  });
}
